// École Nid Douillet - API Service
// Centralized API communication service

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse, LoginCredentials, LoginResponse, ChangePasswordRequest } from '@/types';

// API Configuration
const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000/api';
const API_TIMEOUT = 30000; // 30 seconds

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
class TokenManager {
  private static readonly ACCESS_TOKEN_KEY = 'ecole_access_token';
  private static readonly REFRESH_TOKEN_KEY = 'ecole_refresh_token';

  static getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  static setAccessToken(token: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  static setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  static clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  static setTokens(accessToken: string, refreshToken: string): void {
    this.setAccessToken(accessToken);
    this.setRefreshToken(refreshToken);
  }
}

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = TokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = TokenManager.getRefreshToken();
        if (refreshToken) {
          const response = await apiClient.post('/auth/refresh', {
            refreshToken,
          });

          const { accessToken } = response.data.data;
          TokenManager.setAccessToken(accessToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        TokenManager.clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Base API class
export class BaseApiService {
  protected async request<T>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await apiClient(config);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data) {
          return error.response.data;
        }
        
        // Network or other errors
        return {
          success: false,
          message: error.message || 'Une erreur réseau est survenue',
          code: 'NETWORK_ERROR',
        };
      }
      
      // Unknown error
      return {
        success: false,
        message: 'Une erreur inattendue est survenue',
        code: 'UNKNOWN_ERROR',
      };
    }
  }

  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  protected async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  protected async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  protected async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'PATCH', url, data });
  }

  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }
}

// Authentication API Service
class AuthApiService extends BaseApiService {
  async login(credentials: LoginCredentials): Promise<ApiResponse<LoginResponse['data']>> {
    const response = await this.post<LoginResponse['data']>('/auth/login', credentials);
    
    if (response.success && response.data) {
      TokenManager.setTokens(
        response.data.tokens.accessToken,
        response.data.tokens.refreshToken
      );
    }
    
    return response;
  }

  async logout(): Promise<ApiResponse<void>> {
    const response = await this.post<void>('/auth/logout');
    TokenManager.clearTokens();
    return response;
  }

  async refreshToken(): Promise<ApiResponse<{ accessToken: string; expiresIn: string }>> {
    const refreshToken = TokenManager.getRefreshToken();
    if (!refreshToken) {
      return {
        success: false,
        message: 'Token de rafraîchissement manquant',
        code: 'REFRESH_TOKEN_MISSING',
      };
    }

    const response = await this.post<{ accessToken: string; expiresIn: string }>('/auth/refresh', {
      refreshToken,
    });

    if (response.success && response.data) {
      TokenManager.setAccessToken(response.data.accessToken);
    }

    return response;
  }

  async getCurrentUser(): Promise<ApiResponse<any>> {
    return this.get('/auth/me');
  }

  async changePassword(data: ChangePasswordRequest): Promise<ApiResponse<void>> {
    return this.post<void>('/auth/change-password', data);
  }

  async requestPasswordReset(email: string): Promise<ApiResponse<void>> {
    return this.post<void>('/auth/request-password-reset', { email });
  }

  async validateToken(): Promise<ApiResponse<{ valid: boolean; user: any }>> {
    return this.get('/auth/validate-token');
  }
}

// Children API Service
class ChildrenApiService extends BaseApiService {
  async getChildren(params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<any>> {
    return this.get('/children', { params });
  }

  async getChild(id: string): Promise<ApiResponse<any>> {
    return this.get(`/children/${id}`);
  }

  async createChild(data: any): Promise<ApiResponse<any>> {
    return this.post('/children', data);
  }

  async updateChild(id: string, data: any): Promise<ApiResponse<any>> {
    return this.put(`/children/${id}`, data);
  }

  async deleteChild(id: string): Promise<ApiResponse<void>> {
    return this.delete(`/children/${id}`);
  }
}

// Parents API Service
class ParentsApiService extends BaseApiService {
  async getParents(params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<any>> {
    return this.get('/parents', { params });
  }

  async getParent(id: string): Promise<ApiResponse<any>> {
    return this.get(`/parents/${id}`);
  }

  async createParent(data: any): Promise<ApiResponse<any>> {
    return this.post('/parents', data);
  }

  async updateParent(id: string, data: any): Promise<ApiResponse<any>> {
    return this.put(`/parents/${id}`, data);
  }

  async deleteParent(id: string): Promise<ApiResponse<void>> {
    return this.delete(`/parents/${id}`);
  }
}

// Payments API Service
class PaymentsApiService extends BaseApiService {
  async getPayments(params?: { page?: number; limit?: number; childId?: string; status?: string }): Promise<ApiResponse<any>> {
    return this.get('/payments', { params });
  }

  async getPayment(id: string): Promise<ApiResponse<any>> {
    return this.get(`/payments/${id}`);
  }

  async createPayment(data: any): Promise<ApiResponse<any>> {
    return this.post('/payments', data);
  }

  async updatePayment(id: string, data: any): Promise<ApiResponse<any>> {
    return this.put(`/payments/${id}`, data);
  }

  async deletePayment(id: string): Promise<ApiResponse<void>> {
    return this.delete(`/payments/${id}`);
  }

  async generateReceipt(id: string): Promise<ApiResponse<any>> {
    return this.get(`/payments/${id}/receipt`);
  }
}

// Analytics API Service
class AnalyticsApiService extends BaseApiService {
  async getDashboardStats(): Promise<ApiResponse<any>> {
    return this.get('/analytics/dashboard');
  }

  async getChildrenAnalytics(params?: { academicYear?: string; timeframe?: string }): Promise<ApiResponse<any>> {
    return this.get('/analytics/children', { params });
  }

  async getFinancialAnalytics(params?: { academicYear?: string; timeframe?: string }): Promise<ApiResponse<any>> {
    return this.get('/analytics/financial', { params });
  }

  async getCareServicesAnalytics(params?: { academicYear?: string }): Promise<ApiResponse<any>> {
    return this.get('/analytics/care-services', { params });
  }

  async exportReport(type: string, params?: any): Promise<ApiResponse<any>> {
    return this.get(`/analytics/export/${type}`, { params });
  }
}

// Public API Service (for website)
class PublicApiService extends BaseApiService {
  async getSchoolInfo(): Promise<ApiResponse<any>> {
    return this.get('/public/school-info');
  }

  async getPages(): Promise<ApiResponse<any>> {
    return this.get('/public/pages');
  }

  async getPage(slug: string): Promise<ApiResponse<any>> {
    return this.get(`/public/pages/${slug}`);
  }

  async submitEnrollmentForm(data: any): Promise<ApiResponse<any>> {
    return this.post('/public/enrollment', data);
  }

  async submitContactForm(data: any): Promise<ApiResponse<any>> {
    return this.post('/public/contact', data);
  }
}

// Export API services
export const authApi = new AuthApiService();
export const childrenApi = new ChildrenApiService();
export const parentsApi = new ParentsApiService();
export const paymentsApi = new PaymentsApiService();
export const analyticsApi = new AnalyticsApiService();
export const publicApi = new PublicApiService();

// Export token manager for use in stores
export { TokenManager };

// Export axios instance for custom requests
export { apiClient };

// Health check function
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    const response = await axios.get(`${API_BASE_URL.replace('/api', '')}/health`, {
      timeout: 5000,
    });
    return response.status === 200;
  } catch {
    return false;
  }
};
